import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useLang } from "@/i18n";
import { Reveal, LineMask } from "@/components/Reveal";

const useSundayCountdown = () => {
  const [state, setState] = useState({ units: ["00", "00", "00", "00"], urgent: false });
  useEffect(() => {
    const now = new Date();
    const end = new Date(now);
    end.setDate(now.getDate() + ((7 - now.getDay()) % 7));
    end.setHours(23, 59, 59, 999);
    if (end <= now) end.setDate(end.getDate() + 7);
    const tick = () => {
      const diff = Math.max(0, end - Date.now());
      setState({
        units: [
          Math.floor(diff / 864e5),
          Math.floor(diff / 36e5) % 24,
          Math.floor(diff / 6e4) % 60,
          Math.floor(diff / 1e3) % 60,
        ].map((n) => String(n).padStart(2, "0")),
        urgent: diff > 0 && diff <= 3600e3,
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);
  return state;
};

const Community = () => {
  const { t } = useLang();
  const c = t.community;
  const countdown = useSundayCountdown();

  return (
    <section className="community section-dark" id="community" aria-labelledby="community-title" data-testid="community-section">
      <div className="community-glow" aria-hidden="true"></div>
      <div className="shell">
        <div className="section-heading community-heading">
          <Reveal className="section-index" aria-hidden="true">{c.index}</Reveal>
          <div>
            <Reveal><p className="kicker">{c.kicker}</p></Reveal>
            <h2 id="community-title" className="display-title">
              <LineMask delay={0.1}>{c.titleA}</LineMask>
              <LineMask delay={0.22}><span>{c.titleB}</span></LineMask>
            </h2>
          </div>
        </div>

        <div className="community-layout">
          <Reveal className="challenge-card" data-testid="challenge-card">
            <div className="challenge-head">
              <div><small>{c.challengeLabel}</small><strong>{c.challengeName}</strong></div>
              <span className="live-badge"><i></i> LIVE</span>
            </div>
            <div className="challenge-time">
              <span>12</span><small>{c.challengeUnit}</small>
            </div>
            <div className="challenge-moves">
              {c.moves.map(([n, move]) => (
                <div key={move}><b>{n}</b><span>{move}</span></div>
              ))}
            </div>
            <div className={`countdown ${countdown.urgent ? "urgent" : ""}`} data-testid="challenge-countdown">
              <small>{c.countdown.label}</small>
              <div className="countdown-units">
                {countdown.units.map((v, i) => (
                  <div key={i}><b data-testid={`countdown-unit-${i}`}>{v}</b><span>{c.countdown.units[i]}</span></div>
                ))}
              </div>
            </div>
            <div className="challenge-footer">
              <div className="participants" aria-label="Partecipanti">
                <span>AR</span><span>GM</span><span>LS</span><span>+38</span>
              </div>
              <span>{c.ends}</span>
            </div>
          </Reveal>

          <Reveal className="leaderboard" delay={0.12} data-testid="leaderboard">
            <div className="leaderboard-head">
              <div><small>{c.boardLabel}</small><h3>{c.boardTitle}</h3></div>
              <span>{c.week}</span>
            </div>
            <ol>
              {c.rows.map((r, i) => (
                <motion.li
                  key={r.rank}
                  initial={{ opacity: 0, x: 24 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 + i * 0.1, duration: 0.5 }}
                >
                  <span className="rank">{r.rank}</span>
                  <span className={`person ${r.color}`}>{r.initials}</span>
                  <p><strong>{r.name}</strong><small>{r.sub}</small></p>
                  <b>{r.reps}</b>
                </motion.li>
              ))}
              <motion.li
                className="current"
                initial={{ opacity: 0, scale: 0.96 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.55, duration: 0.5 }}
              >
                <span className="rank">{c.you.rank}</span>
                <span className="person white">{c.you.initials}</span>
                <p><strong>{c.you.name}</strong><small>{c.you.sub}</small></p>
                <b>{c.you.reps}</b>
              </motion.li>
            </ol>
            <p className="demo-note">{c.demoNote}</p>
          </Reveal>
        </div>

        <div className="community-features">
          {c.features.map((f, i) => (
            <Reveal key={f.title} as="article" delay={i * 0.1}>
              <span className="feature-icon" aria-hidden="true">{f.icon}</span>
              <h3>{f.title}</h3>
              <p>{f.text}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Community;
